import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Block as BlockIcon,
  PictureAsPdf as PdfIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import sucVentaService from '../../services/sucVentaService';
import remitoVentaService, {
  RemitoVenta,
  RemitoVentaDetalleDto,
  remitoDetalleCantidadDisplay,
  remitoDetalleUmDisplay,
} from '../../services/remitoVentaService';
import {
  cantidadDoc,
  labelsExtra,
  showExtraPedFac,
  umDoc,
} from '../../utils/umDocumentoDisplay';
import ubicacionService from '../../services/ubicacionService';
import loteInformesService from '../../services/loteInformesService';
import type { LoteAsignacion } from '../../services/loteService';
import LoteDetalleReadonly from '../../components/lotes/LoteDetalleReadonly';
import NumeroInternoMeta from '../../components/NumeroInternoMeta';
import PDFViewer from '../../components/shared/PDFViewer';
import { formatDate, formatNroDocumentoVenta } from '../../utils/formatters';
import { getApiErrorMessage } from '../../utils/apiError';
import { getFileNameFromContentDisposition } from '../../utils/contentDisposition';
import { esRemitoCompraAnulado } from '../../utils/remitoPuedeAnular';
import { ROUTES } from '../../config/routes';

const formatearFecha = (fecha: Date | string | null | undefined): string => {
  if (!fecha) return '-';
  const d = typeof fecha === 'string' ? new Date(fecha) : fecha;
  if (isNaN(d.getTime())) return '-';
  return formatDate(d.toISOString().split('T')[0]);
};

const enriquecerLotesDesdeMovimientos = async (data: RemitoVenta): Promise<RemitoVenta> => {
  const detalles = data.detalles ?? [];
  const yaTiene = detalles.some((d) => (d.lotes?.length ?? 0) > 0);
  if (yaTiene || !data.nroFormulario) return data;
  try {
    const movs = await loteInformesService.movimientos({
      nroIDFormulario: data.nroFormulario,
      pageSize: 500,
    });
    const items = (movs.items ?? []).filter(
      (m) => Number(m.nroIDFormulario) === Number(data.nroFormulario),
    );
    if (items.length === 0) return data;

    const byItem = new Map<number, LoteAsignacion[]>();
    for (const m of items) {
      const nroItem = Number(m.nroItem) || 0;
      const list = byItem.get(nroItem) ?? [];
      list.push({
        idLote: m.idLote,
        nroLote: m.nroLote,
        cantidad: Math.abs(Number(m.cantidad) || 0),
        fechaVencimiento: null,
      });
      byItem.set(nroItem, list);
    }

    const mergeLotes = (lineas: RemitoVentaDetalleDto[]) =>
      lineas.map((d) => {
        if ((d.lotes?.length ?? 0) > 0) return d;
        const fromItem = byItem.get(Number(d.nroItem) || 0);
        if (fromItem?.length) return { ...d, lotes: fromItem };
        const byCodigo = items
          .filter(
            (m) =>
              String(m.codigoArticulo ?? '').trim().toUpperCase() ===
              String(d.codigoArticulo ?? '').trim().toUpperCase(),
          )
          .map(
            (m): LoteAsignacion => ({
              idLote: m.idLote,
              nroLote: m.nroLote,
              cantidad: Math.abs(Number(m.cantidad) || 0),
              fechaVencimiento: null,
            }),
          );
        return byCodigo.length ? { ...d, lotes: byCodigo } : d;
      });

    return { ...data, detalles: mergeLotes(detalles) };
  } catch {
    return data;
  }
};

const RemitoVentaDetalle: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { nroFormulario } = useParams<{ nroFormulario: string }>();
  const { enqueueSnackbar } = useSnackbar();

  const [remito, setRemito] = useState<RemitoVenta | null>(null);
  const [ubicacionOrigenNombre, setUbicacionOrigenNombre] = useState('');
  const [loading, setLoading] = useState(true);
  const [anulando, setAnulando] = useState(false);
  const [anularOpen, setAnularOpen] = useState(false);
  const [requiereClaveAnulacion, setRequiereClaveAnulacion] = useState(false);
  const [claveSuperusuario, setClaveSuperusuario] = useState('');
  const [motivoAnulacion, setMotivoAnulacion] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfViewer, setPdfViewer] = useState<{ url: string; fileName: string; title: string } | null>(
    null,
  );

  const loadRemito = useCallback(async () => {
    const id = parseInt(nroFormulario || '', 10);
    if (Number.isNaN(id)) {
      enqueueSnackbar('Remito no válido', { variant: 'error' });
      navigate(ROUTES.ventas.remitos.list);
      return;
    }

    try {
      setLoading(true);
      const raw = await remitoVentaService.getRemitoVenta(id);
      const data = await enriquecerLotesDesdeMovimientos(raw);
      setRemito(data);

      if (data?.nroUbicacionOrigen) {
        try {
          const u = await ubicacionService.getUbicacion(data.nroUbicacionOrigen);
          setUbicacionOrigenNombre(u?.nombreUbicacion?.trim() || '');
        } catch {
          setUbicacionOrigenNombre('');
        }
      } else {
        setUbicacionOrigenNombre('');
      }
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar el remito de venta'), {
        variant: 'error',
      });
      navigate(ROUTES.ventas.remitos.list);
    } finally {
      setLoading(false);
    }
  }, [nroFormulario, enqueueSnackbar, navigate]);

  useEffect(() => {
    void loadRemito();
  }, [loadRemito]);

  useEffect(() => {
    return () => {
      if (pdfViewer?.url) URL.revokeObjectURL(pdfViewer.url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nroComprobanteTxt = remito
    ? formatNroDocumentoVenta(remito.letra, remito.nroSuc, remito.nroComprobante) ||
      remito.nroRemito ||
      ''
    : '';

  const handleImprimirPdf = async () => {
    if (!remito) return;
    try {
      setPdfLoading(true);
      const response = await api.get(`/api/RemitosVenta/${remito.nroFormulario}/pdf`, {
        responseType: 'blob',
      });
      const cd = (response.headers as Record<string, string>)?.['content-disposition'];
      const fileName =
        getFileNameFromContentDisposition(cd) ?? `Remito_${remito.nroFormulario}.pdf`;
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfViewer((prev) => {
        if (prev?.url) URL.revokeObjectURL(prev.url);
        return {
          url,
          fileName,
          title: `Remito ${nroComprobanteTxt || remito.nroFormulario}`,
        };
      });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo generar el PDF'), { variant: 'error' });
    } finally {
      setPdfLoading(false);
    }
  };

  const handleAbrirAnulacion = async () => {
    if (!remito) return;
    setClaveSuperusuario('');
    setMotivoAnulacion('');
    setRequiereClaveAnulacion(false);
    try {
      const suc = await sucVentaService.getSucVenta(remito.nroSuc, '9');
      setRequiereClaveAnulacion(Boolean((suc.passAnulacion ?? '').trim()));
    } catch {
      setRequiereClaveAnulacion(false);
    }
    setAnularOpen(true);
  };

  const handleConfirmarAnulacion = async () => {
    if (!remito) return;
    if (requiereClaveAnulacion && !claveSuperusuario.trim()) return;
    try {
      setAnulando(true);
      await remitoVentaService.anularRemitoVenta(remito.nroFormulario, {
        ...(requiereClaveAnulacion
          ? { claveSuperusuario: claveSuperusuario.trim() }
          : {}),
        motivo: motivoAnulacion.trim() || undefined,
      });
      enqueueSnackbar('Remito anulado y movimiento de stock revertido.', { variant: 'success' });
      setAnularOpen(false);
      setRequiereClaveAnulacion(false);
      setClaveSuperusuario('');
      setMotivoAnulacion('');
      await loadRemito();
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo anular el remito.'), {
        variant: 'error',
      });
    } finally {
      setAnulando(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#1976d2' }} />
      </Box>
    );
  }

  if (!remito) return null;

  const detalles = remito.detalles || [];
  const anulado = esRemitoCompraAnulado(remito);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 3 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(ROUTES.ventas.remitos.list)}
              sx={{ textTransform: 'none' }}
            >
              Remitos de Venta
            </Button>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: '#1976d2',
                fontSize: { xs: '1.2rem', sm: '2.125rem' },
                wordBreak: 'break-word',
                flex: 1,
              }}
            >
              Remito de Venta {nroComprobanteTxt || remito.nroFormulario}
            </Typography>
            {!anulado && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}
              >
                <Button
                  variant="outlined"
                  startIcon={pdfLoading ? <CircularProgress size={16} /> : <PdfIcon />}
                  disabled={pdfLoading}
                  onClick={() => void handleImprimirPdf()}
                  sx={{ textTransform: 'none' }}
                >
                  Imprimir PDF
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<BlockIcon />}
                  onClick={() => void handleAbrirAnulacion()}
                  sx={{ textTransform: 'none' }}
                >
                  Anular
                </Button>
              </Stack>
            )}
          </Stack>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2', fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
            Encabezado
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Fecha emisión
              </Typography>
              <Typography variant="body1">{formatearFecha(remito.fechaEmision)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Nro Comprobante
              </Typography>
              <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                {nroComprobanteTxt || '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Cliente
              </Typography>
              <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                {remito.nroCliente != null ? remito.nroCliente : '-'}
                {remito.nombre ? ` - ${remito.nombre}` : ''}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Ubicación origen
              </Typography>
              <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                {remito.nroUbicacionOrigen != null
                  ? ubicacionOrigenNombre
                    ? `${remito.nroUbicacionOrigen} - ${ubicacionOrigenNombre}`
                    : String(remito.nroUbicacionOrigen)
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Usuario
              </Typography>
              <Typography variant="body1">{remito.usuario || '-'}</Typography>
            </Grid>
            {(remito.bultos ?? 0) > 0 && (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Bultos
                </Typography>
                <Typography variant="body1">{remito.bultos}</Typography>
              </Grid>
            )}
            {remito.descripcion && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Descripción
                </Typography>
                <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                  {remito.descripcion}
                </Typography>
              </Grid>
            )}
          </Grid>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2', fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
            Detalle
          </Typography>

          {detalles.length === 0 ? (
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Sin ítems.
            </Typography>
          ) : isMobile ? (
            <Stack spacing={1.5} sx={{ mb: 3 }}>
              {detalles.map((d, idx) => (
                <Card key={`${d.nroIdformulario}-${d.nroItem}-${idx}`} variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack spacing={1}>
                      <Box
                        sx={(t) => ({
                          p: 1,
                          borderRadius: 1.5,
                          backgroundColor: t.palette.primary.light + '14',
                        })}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                          {d.codigoArticulo || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {d.descripcionArticulo || '-'}
                        </Typography>
                      </Box>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">
                          Cantidad
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {remitoDetalleCantidadDisplay(d).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                          {remitoDetalleUmDisplay(d) ? ` ${remitoDetalleUmDisplay(d)}` : ''}
                        </Typography>
                      </Stack>
                      {showExtraPedFac(d, 'ped') ? (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">
                            {labelsExtra('ped').cant} / {labelsExtra('ped').um}
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {(cantidadDoc(d, 'ped') ?? 0).toLocaleString('es-AR', {
                              minimumFractionDigits: 2,
                            })}
                            {umDoc(d, 'ped') ? ` ${umDoc(d, 'ped')}` : ''}
                          </Typography>
                        </Stack>
                      ) : null}
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Lotes
                        </Typography>
                        <LoteDetalleReadonly
                          codigoArticulo={d.codigoArticulo}
                          cantidad={Number(d.cantidad) || 0}
                          nroUbicacion={remito.nroUbicacionOrigen}
                          mode="egreso"
                          lotes={d.lotes}
                          dense
                        />
                      </Box>
                      {d.notas ? (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Notas
                          </Typography>
                          <Typography variant="body2">{d.notas}</Typography>
                        </Box>
                      ) : null}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer sx={{ mb: 3, maxWidth: '100%', overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Cantidad
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>UM</TableCell>
                    {detalles.some((d) => showExtraPedFac(d, 'ped')) ? (
                      <>
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          Cant. Ped
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>UM Ped</TableCell>
                      </>
                    ) : null}
                    <TableCell sx={{ fontWeight: 600 }}>Lotes</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Notas</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detalles.map((d, idx) => (
                    <TableRow key={`${d.nroIdformulario}-${d.nroItem}-${idx}`}>
                      <TableCell>{d.codigoArticulo || '-'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {d.descripcionArticulo || '-'}
                      </TableCell>
                      <TableCell align="right">
                        {remitoDetalleCantidadDisplay(d).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell>{remitoDetalleUmDisplay(d) || '-'}</TableCell>
                      {detalles.some((x) => showExtraPedFac(x, 'ped')) ? (
                        <>
                          <TableCell align="right">
                            {showExtraPedFac(d, 'ped')
                              ? (cantidadDoc(d, 'ped') ?? 0).toLocaleString('es-AR', {
                                  minimumFractionDigits: 2,
                                })
                              : '—'}
                          </TableCell>
                          <TableCell>{showExtraPedFac(d, 'ped') ? umDoc(d, 'ped') || '—' : '—'}</TableCell>
                        </>
                      ) : null}
                      <TableCell sx={{ minWidth: 140 }}>
                        <LoteDetalleReadonly
                          codigoArticulo={d.codigoArticulo}
                          cantidad={Number(d.cantidad) || 0}
                          nroUbicacion={remito.nroUbicacionOrigen}
                          mode="egreso"
                          lotes={d.lotes}
                          dense
                        />
                      </TableCell>
                      <TableCell>{d.notas || ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <NumeroInternoMeta nro={remito.nroFormulario} />
        </CardContent>
      </Card>

      <Dialog
        open={anularOpen}
        onClose={() => !anulando && setAnularOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main', fontWeight: 600 }}>
          Anular remito y revertir stock
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography>
              Esta acción anula el remito y revierte su movimiento de stock.
              {requiereClaveAnulacion
                ? ' Ingresá la clave de anulación configurada en Tipos de formulario por Sucursal.'
                : ''}
            </Typography>
            <Typography>
              <strong>Remito:</strong> {nroComprobanteTxt || remito.nroFormulario}
            </Typography>
            {requiereClaveAnulacion && (
              <TextField
                autoFocus
                fullWidth
                type="password"
                label="Clave de anulación"
                value={claveSuperusuario}
                onChange={(e) => setClaveSuperusuario(e.target.value)}
                autoComplete="new-password"
                disabled={anulando}
                inputProps={{ maxLength: 72 }}
              />
            )}
            <TextField
              fullWidth
              label="Motivo (opcional)"
              value={motivoAnulacion}
              onChange={(e) => setMotivoAnulacion(e.target.value)}
              disabled={anulando}
              multiline
              minRows={2}
              autoFocus={!requiereClaveAnulacion}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnularOpen(false)} disabled={anulando}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleConfirmarAnulacion()}
            disabled={anulando || (requiereClaveAnulacion && claveSuperusuario.length === 0)}
            startIcon={anulando ? <CircularProgress size={18} color="inherit" /> : <UndoIcon />}
          >
            {anulando ? 'Procesando…' : 'Anular y revertir stock'}
          </Button>
        </DialogActions>
      </Dialog>

      {pdfViewer ? (
        <PDFViewer
          pdfUrl={pdfViewer.url}
          title={pdfViewer.title}
          documentId={String(remito.nroFormulario)}
          documentType="remito"
          downloadFileName={pdfViewer.fileName}
          onClose={() => {
            URL.revokeObjectURL(pdfViewer.url);
            setPdfViewer(null);
          }}
        />
      ) : null}
    </Box>
  );
};

export default RemitoVentaDetalle;
